/**
 * API Service for Messaging Service (messaging-service-go)
 * Handles conversations, messages, and WebSocket connections
 * Base URL: Configured via environment variable
 */

import axios from 'axios';
import { Conversation, Message, DavidConversation, DavidMessageResponse } from '../types';

// Get base URL from environment or use default
const MESSAGING_SERVICE_BASE_URL = import.meta.env.VITE_MESSAGING_SERVICE_URL || 'http://localhost:8080';

/**
 * Response from GET /conversations endpoint
 */
export interface GetConversationsResponse {
  conversations: Conversation[];
  has_more: boolean;
}

/**
 * Response from GET /messages endpoint
 */
export interface GetMessagesResponse {
  messages: Message[];
  has_more: boolean;
  before?: string;
  after?: string;
}

/**
 * Pagination options for fetching messages
 */
export interface MessagePaginationOptions {
  application_id: string;
  limit?: number;
  before?: string; // Cursor for pagination (message ID)
  after?: string;  // Cursor for pagination (message ID)
}

/**
 * Pagination options for fetching conversations
 */
export interface ConversationPaginationOptions {
  limit?: number;  // Max 100, default 20
  skip?: number;   // Offset, default 0
}

class MessagingServiceApi {
  private baseURL: string;

  constructor() {
    this.baseURL = MESSAGING_SERVICE_BASE_URL;
  }

  /**
   * Get auth token from localStorage
   * Uses the same token storage as the auth system
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  /**
   * Get auth headers with Bearer token
   */
  private getAuthHeaders(): Record<string, string> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Get all conversations for the current user
   * Returns paginated list of conversations with last message and unread counts
   *
   * @param options Pagination options (limit, skip)
   * @returns Promise with conversations array and has_more flag
   */
  async getConversations(
    options: ConversationPaginationOptions = {}
  ): Promise<GetConversationsResponse> {
    const { limit = 20, skip = 0 } = options;

    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('skip', skip.toString());

    const response = await axios.get<{ status: string; data: GetConversationsResponse }>(
      `${this.baseURL}/conversations?${params.toString()}`,
      {
        headers: this.getAuthHeaders(),
      }
    );

    // Backend wraps response in {status, data} structure
    return response.data.data;
  }

  /**
   * Get messages for a specific conversation (application)
   * Returns paginated message history
   *
   * @param options Pagination options with application_id
   * @returns Promise with messages array and pagination cursors
   */
  async getMessages(
    options: MessagePaginationOptions
  ): Promise<GetMessagesResponse> {
    const { application_id, limit = 50, before, after } = options;

    if (!application_id) {
      throw new Error('application_id is required');
    }

    const params = new URLSearchParams();
    params.append('application_id', application_id);
    params.append('limit', limit.toString());
    if (before) params.append('before', before);
    if (after) params.append('after', after);

    const response = await axios.get<{ status: string; data: GetMessagesResponse }>(
      `${this.baseURL}/messages?${params.toString()}`,
      {
        headers: this.getAuthHeaders(),
      }
    );

    // Backend wraps response in {status, data} structure
    return response.data.data;
  }

  /**
   * Mark a conversation as read
   * Resets unread count for the current user
   *
   * @param applicationId Application ID (conversation ID)
   * @returns Promise that resolves when marked as read
   */
  async markConversationAsRead(applicationId: string): Promise<void> {
    if (!applicationId) {
      throw new Error('applicationId is required');
    }

    await axios.post(
      `${this.baseURL}/conversations/read?application_id=${applicationId}`,
      {},
      {
        headers: this.getAuthHeaders(),
      }
    );
  }

  /**
   * Get WebSocket URL for real-time messaging
   * Constructs WebSocket URL with authentication token and application ID
   *
   * @param applicationId Application ID for the conversation
   * @returns WebSocket URL with query parameters
   */
  getWebSocketUrl(applicationId: string): string {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Convert http/https to ws/wss
    const wsBaseURL = this.baseURL.replace(/^http/, 'ws');

    return `${wsBaseURL}/ws?application_id=${applicationId}&token=${encodeURIComponent(token)}`;
  }

  /**
   * Get David (bot) conversations for the current candidate
   * Returns conversations where David sent job recommendations
   */
  async getDavidConversations(): Promise<DavidConversation[]> {
    const response = await axios.get<{ conversations: any[] }>(
      `${this.baseURL}/david/conversations`,
      { headers: this.getAuthHeaders() }
    );
    const raw = response.data as any;
    const conversations = raw?.data?.conversations || raw?.conversations || [];
    return conversations.map((c: any) => ({
      id: c.id,
      candidateId: c.candidate_id,
      candidateName: c.candidate_name,
      candidateEmail: c.candidate_email,
      lastMessage: c.last_message ? {
        content: c.last_message.content,
        senderId: c.last_message.sender_id,
        timestamp: c.last_message.timestamp,
      } : undefined,
      createdAt: c.created_at,
      updatedAt: c.updated_at,
    }));
  }

  /**
   * Get messages in a David conversation
   */
  async getDavidMessages(conversationId: string): Promise<DavidMessageResponse[]> {
    const response = await axios.get<{ messages: DavidMessageResponse[] }>(
      `${this.baseURL}/david/messages?conversation_id=${conversationId}`,
      { headers: this.getAuthHeaders() }
    );
    const raw = response.data as any;
    return raw?.data?.messages || raw?.messages || [];
  }

  /**
   * Submit thumbs up/down feedback on a David message
   */
  async submitFeedback(messageId: string, type: 'thumbs_up' | 'thumbs_down'): Promise<void> {
    await axios.post(
      `${this.baseURL}/david/messages/feedback`,
      { message_id: messageId, type },
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Submit a quick response chip selection on a David message
   */
  async submitQuickResponse(messageId: string, text: string): Promise<void> {
    await axios.post(
      `${this.baseURL}/david/messages/quick-response`,
      { message_id: messageId, text },
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Health check endpoint
   * Useful for checking if the messaging service is available
   *
   * @returns Promise that resolves if service is healthy
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await axios.get(`${this.baseURL}/health`);
      return response.status === 200;
    } catch (error) {
      console.error('Messaging service health check failed:', error);
      return false;
    }
  }
}

export const messagingServiceApi = new MessagingServiceApi();
